export const STATUS_COMPLETED = "COMPLETED";
export const STATUS_CANCELLED = "CANCELLED";

export function allRequiredStepsCaptured(steps, stepValues) {
  if (!Array.isArray(steps) || steps.length === 0) return false;
  return steps.every((step) => {
    if (step.required === false) return true;
    const stepId = step.stepId;
    if (!stepId) return false;
    const captured = stepValues?.[stepId];
    return captured != null && String(captured).trim().length > 0;
  });
}

export function applyGuideSnapshot(
  snapshot,
  { setCurrentStepIndex, setStepValues }
) {
  if (!snapshot || typeof snapshot !== "object") return;
  if (typeof snapshot.currentStepIndex === "number") {
    setCurrentStepIndex(snapshot.currentStepIndex);
  }
  if (snapshot.stepValues && typeof snapshot.stepValues === "object") {
    setStepValues(snapshot.stepValues);
  }
}

export function submitValueForStep(step, inputDraft) {
  if (!step) return "";
  switch (step.type) {
    case "paste_input":
      return inputDraft.trim();
    case "single_choice":
      return inputDraft;
    case "open_url":
      return "opened";
    case "confirm":
      return "confirmed";
    case "copy_value":
      return "copied";
    case "instruction":
    default:
      return "acknowledged";
  }
}

export function isSubmitValueUnchanged(step, stepValues, inputDraft) {
  const stepId = step?.stepId;
  if (!stepId || !(stepId in (stepValues || {}))) return false;
  const next = submitValueForStep(step, inputDraft);
  return String(stepValues[stepId]) === String(next);
}

export function formatCapturedLabel(step, raw) {
  if (!raw) return "";
  switch (step?.type) {
    case "open_url":
      return "Link opened";
    case "confirm":
      return "Confirmed";
    case "copy_value":
      return "Copied";
    case "instruction":
      return "Acknowledged";
    default:
      return raw;
  }
}

/**
 * Single source of truth for the guide's step-position math. Every consumer (the card body, the
 * clamp effect, the snapshot-hydrate effect, and the post-advance handler) derives its numbers
 * from this one function so the "furthest viewable step" invariant is expressed exactly once.
 *
 * @returns {{
 *   total: number,
 *   lastIndex: number,
 *   frontierIndex: number,
 *   allCaptured: boolean,
 *   awaitingCompleteOnly: boolean,
 *   maxViewIndex: number,
 *   effectiveCurrentStepIndex: number,
 *   completedCount: number,
 * }}
 */
export function deriveGuideView({
  steps,
  currentStepIndex,
  stepValues,
  isTerminal = false,
  terminalStatus = null,
}) {
  const total = Array.isArray(steps) ? steps.length : 0;
  const lastIndex = Math.max(0, total - 1);
  const frontierIndex = currentStepIndex + 1;
  const allCaptured = allRequiredStepsCaptured(steps, stepValues);
  const awaitingCompleteOnly = allCaptured && currentStepIndex >= total - 1;
  const maxViewIndex =
    total === 0
      ? 0
      : isTerminal || awaitingCompleteOnly
        ? lastIndex
        : Math.min(frontierIndex, lastIndex);
  const effectiveCurrentStepIndex = isTerminal
    ? Math.max(currentStepIndex, lastIndex)
    : currentStepIndex;
  const completedCount =
    isTerminal && terminalStatus === STATUS_COMPLETED
      ? total
      : Math.max(0, currentStepIndex + 1);

  return {
    total,
    lastIndex,
    frontierIndex,
    allCaptured,
    awaitingCompleteOnly,
    maxViewIndex,
    effectiveCurrentStepIndex,
    completedCount,
  };
}

/**
 * The viewing index to land on after a snapshot is applied: the furthest viewable step for that
 * snapshot. Identical to {@link deriveGuideView}'s `maxViewIndex` for the snapshot's progress.
 */
export function resolveViewIndexAfterSnapshot(snapshot, steps) {
  const currentStepIndex =
    typeof snapshot?.currentStepIndex === "number"
      ? snapshot.currentStepIndex
      : -1;
  return deriveGuideView({
    steps,
    currentStepIndex,
    stepValues: snapshot?.stepValues || {},
  }).maxViewIndex;
}
